import { useAuthHydration } from '@/hooks/use-auth-hydration';
import { getApiHostHeaders, requireApiBaseUrl } from '@/lib/api-url';
import { appendCompressedImage } from '@/lib/image-upload';
import api from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getFetchErrorMessage } from '@/utils/api-error';

/** Máximo de fotos por serviço (espelha o backend). */
export const SERVICE_PHOTO_MAX = 3;

export interface ServicePhoto {
  id: string;
  url: string;
  position: number;
  createdAt: string;
}

export const servicePhotoKeys = {
  all: ['service-photos'] as const,
  list: (serviceId: string) => [...servicePhotoKeys.all, serviceId] as const,
};

export function useServicePhotos(serviceId: string | undefined) {
  const isHydrated = useAuthHydration();

  return useQuery({
    queryKey: servicePhotoKeys.list(serviceId ?? ''),
    queryFn: async () => {
      const response = await api.get<ServicePhoto[]>(
        `/provider/me/services/${serviceId}/photos`,
      );
      return response.data;
    },
    enabled: isHydrated && !!serviceId,
  });
}

export class ServicePhotoError extends Error {
  code?: string;

  constructor(message: string, code?: string) {
    super(message);
    this.name = 'ServicePhotoError';
    this.code = code;
  }
}

export function useAddServicePhoto(serviceId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { imageUri: string }) => {
      if (!serviceId) throw new ServicePhotoError('Serviço inválido.');

      const formData = new FormData();
      await appendCompressedImage(formData, 'file', input.imageUri, 'photo');

      const API_URL = requireApiBaseUrl().replace(/\/$/, '');
      const token = useAuthStore.getState().token;

      const response = await fetch(
        `${API_URL}/provider/me/services/${serviceId}/photos`,
        {
          method: 'POST',
          body: formData,
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
            ...getApiHostHeaders(),
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new ServicePhotoError(
          getFetchErrorMessage(response.status, errorData, 'Erro ao enviar foto.'),
          errorData?.code,
        );
      }

      return (await response.json()) as ServicePhoto;
    },
    onSuccess: () => {
      if (serviceId) {
        void queryClient.invalidateQueries({
          queryKey: servicePhotoKeys.list(serviceId),
        });
      }
    },
  });
}

export function useDeleteServicePhoto(serviceId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (photoId: string) => {
      await api.delete(`/provider/me/services/${serviceId}/photos/${photoId}`);
    },
    onSuccess: () => {
      if (serviceId) {
        void queryClient.invalidateQueries({
          queryKey: servicePhotoKeys.list(serviceId),
        });
      }
    },
  });
}
